import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  Timestamp ,
  orderBy,
  limit,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faCalendarCheck, 
  faRoute, 
  faEnvelope,
  faUser,
  faCog,
  faPlus,
  faTimes,
  faSpinner,
  faThumbsUp,
  faComment,
  faEllipsisV,
  faCheck,
  faClock,
  faFlag,
  faUpload,
  faUserPlus,
  faLock,
  faCalendarAlt,
  faUserShield,
  faBuilding,
  faStar,
  faStarHalf,
  faChalkboardTeacher,
  faUserTie,
  faUsers,
  faPhone,
  faHandshake,
  faIdCard,
  faBriefcase,
  faStore,
  faTrash,
  faMusic
} from '@fortawesome/free-solid-svg-icons';
import { createNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import UserContext from '../../context/UserContext';
import styles from './Profile.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';

// Export all the components
export { 
  FeedbackTab,
  ReviewsTab,
  StarRating,
  OrganizationsSection,
  StaffSection,
  AssociatedVendorsSection,
  ManagingActsSection,
  ManagersSection,
  ManagedBySection,
  ExecutivesSection,
  ContactsSection
};

const FeedbackTab = () => {
    return (
      <div className={styles.placeholderSection}>
        <div className={styles.placeholderIcon}>
          <FontAwesomeIcon icon={faComment} />
        </div>
        <h3>Feedback Coming Soon</h3>
        <p>We're working on implementing a feedback system to help you collect and manage feedback from your events and collaborations.</p>
      </div>
    );
  };
  
  // New Component for Reviews Tab (placeholder)
  const ReviewsTab = () => {
    return (
      <div className={styles.placeholderSection}>
        <div className={styles.placeholderIcon}>
          <FontAwesomeIcon icon={faStar} />
        </div>
        <h3>Reviews Coming Soon</h3>
        <p>The reviews section is currently under development. This will allow users to leave ratings and detailed reviews about their experiences.</p>
      </div>
    );
  };
  
  // Component for displaying star ratings
  const StarRating = ({ rating = 0 }) => {
    // Convert rating to a number between 0 and 5
    const numericRating = Math.min(5, Math.max(0, Number(rating) || 0));
    
    // Calculate whole stars and partial stars
    const fullStars = Math.floor(numericRating);
    const hasHalfStar = numericRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <div className={styles.starRating}>
        {[...Array(fullStars)].map((_, i) => (
          <FontAwesomeIcon key={`full-${i}`} icon={faStar} className={styles.starFilled} />
        ))}
        
        {hasHalfStar && (
          <FontAwesomeIcon icon={faStarHalf} className={styles.starFilled} />
        )}
        
        {[...Array(emptyStars)].map((_, i) => (
          <FontAwesomeIcon key={`empty-${i}`} icon={faStar} className={styles.starEmpty} />
        ))}
        
        <span className={styles.ratingText}>{numericRating.toFixed(1)}</span>
      </div>
    );
  };
  
  // New Component for Organizations Section
  const OrganizationsSection = ({ profileData, isOwnProfile, showCustomAlert, navigate }) => {
    const [organizations, setOrganizations] = useState([]);
    const [displayedOrganizations, setDisplayedOrganizations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const { userData, authToken } = useContext(UserContext);
    
    useEffect(() => {
      if (profileData?.userId) {
        fetchOrganizations(profileData.userId);
      }
    }, [profileData?.userId]);
    
    const fetchOrganizations = async (userId) => {
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        
        // First get the user document to get organization count and top 3 organizations
        const userDoc = await getDoc(doc(db, 'users', userId));
        let initialOrganizations = [];
        let totalCount = 0;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          initialOrganizations = userData.organizations || [];
          totalCount = userData.organizationsCount || 0;
          
          // Enrich with organization data if needed
          if (initialOrganizations.length > 0) {
            initialOrganizations = await Promise.all(
              initialOrganizations.map(async (org) => {
                // Only fetch full organization details if we just have the ID
                if (org.organizationId && (!org.organizationName || !org.organizationType)) {
                  try {
                    const orgDoc = await getDoc(doc(db, 'users', org.organizationId));
                    if (orgDoc.exists()) {
                      const orgData = orgDoc.data();
                      return {
                        ...org,
                        organizationName: orgData.fullName || '',
                        userName: orgData.userName || '',
                        profilePictureUrl: orgData.profilePictureUrl || '',
                        organizationType: orgData.userSubType || ''
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching organization data for ${org.organizationId}:`, error);
                  }
                }
                return org;
              })
            );
          }
        }
        
        // Set initial displayed organizations
        setDisplayedOrganizations(initialOrganizations);
        
        // Check if there are more organizations than what's displayed
        setHasMore(totalCount > initialOrganizations.length);
        
        // If there are organizations, fetch all from subcollection for complete data
        if (initialOrganizations.length > 0) {
          const orgsRef = collection(db, 'users', userId, 'organizations');
          const snapshot = await getDocs(orgsRef);
          
          if (!snapshot.empty) {
            const allOrgs = await Promise.all(
              snapshot.docs.map(async (docSnapshot) => {
                const orgData = docSnapshot.data();
                
                // Enrich with organization user data
                if (orgData.organizationId) {
                  try {
                    const orgUserDoc = await getDoc(doc(db, 'users', orgData.organizationId));
                    if (orgUserDoc.exists()) {
                      const orgUserData = orgUserDoc.data();
                      return {
                        id: docSnapshot.id,
                        ...orgData,
                        organizationName: orgUserData.fullName || '',
                        userName: orgUserData.userName || '',
                        profilePictureUrl: orgUserData.profilePictureUrl || '',
                        organizationType: orgUserData.userSubType || ''
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching organization user data for ${orgData.organizationId}:`, error);
                  }
                }
                
                // Return the organization data as is if user data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...orgData
                };
              })
            );
            
            // Store all organizations
            setOrganizations(allOrgs);
          } else {
            setOrganizations(initialOrganizations);
          }
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        showCustomAlert({
          title: 'Error',
          message: 'Failed to load organizations',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    const handleOrganizationClick = (org) => {
      if (org.userName) {
        navigate(`/${org.userName}`);
      } else if (org.organizationId) {
        // Navigate to organization profile by ID
        navigate(`/users/${org.organizationId}`);
      }
    };
    
    const handleLoadMore = async () => {
      if (loadingMore || !hasMore || !profileData?.userId) return;
      
      setLoadingMore(true);
      try {
        // Load more organizations from the full list
        const nextBatch = organizations.slice(
          displayedOrganizations.length,
          displayedOrganizations.length + 3
        );
        
        setDisplayedOrganizations(prev => [...prev, ...nextBatch]);
        
        // Check if there are more organizations to load
        setHasMore(displayedOrganizations.length + nextBatch.length < organizations.length);
      } catch (error) {
        console.error('Error loading more organizations:', error);
        showCustomAlert({
          title: 'Error',
          message: 'Failed to load more organizations',
          type: 'error'
        });
      } finally {
        setLoadingMore(false);
      }
    };
    
    const handleRemoveOrganization = async (organizationId, organizationName) => {
      if (!window.confirm(`Are you sure you want to remove ${organizationName || 'this organization'}?`)) {
        return;
      }
      
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        const userRef = doc(db, 'users', profileData.userId);
        
        // 1. Remove from subcollection
        const organizationRef = doc(db, 'users', profileData.userId, 'organizations', organizationId);
        await deleteDoc(organizationRef);
        
        // 2. Update the main user document by removing from the organizations array
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const updatedOrganizations = (userData.organizations || []).filter(
            org => org.organizationId !== organizationId
          );
          
          // 3. Get new top 3 organizations for main document
          const orgsRef = collection(db, 'users', profileData.userId, 'organizations');
          const top3Query = query(orgsRef, orderBy('joinedAt', 'desc'), limit(3));
          const top3Snapshot = await getDocs(top3Query);
          const top3Organizations = top3Snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // 4. Update main document with top 3 and decrement count
          await updateDoc(userRef, {
            organizations: top3Organizations,
            organizationsCount: Math.max(0, (userData.organizationsCount || 0) - 1)
          });
          
          // 5. Create removal notification for the organization
          await createNotification(organizationId, 'general', {
            type: 'ORGANIZATION_STAFF_REMOVAL',
            title: 'Staff Removal',
            message: `${userData.fullName || 'A user'} has removed themselves as staff from your organization.`,
            data: {
              userId: profileData.userId,
              userName: userData.userName,
              fullName: userData.fullName,
              _displayData: {
                userName: userData.userName,
                fullName: userData.fullName
              }
            }
          });
          
          // 6. Update UI state
          const updatedOrgs = organizations.filter(org => org.organizationId !== organizationId);
          setOrganizations(updatedOrgs);
          setDisplayedOrganizations(displayedOrganizations.filter(
            org => org.organizationId !== organizationId
          ));
          
          showCustomAlert({
            title: 'Success',
            message: `Successfully removed from ${organizationName || 'organization'}`,
            type: 'success'
          });
        }
      } catch (error) {
        console.error('Error removing organization:', error);
        showCustomAlert({
          title: 'Error',
          message: 'Failed to remove organization',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    return (
      <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
        <h3 className={styles.sectionTitle}>Organizations</h3>
        
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading organizations...</p>
          </div>
        ) : displayedOrganizations.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedOrganizations.map(org => (
              <div 
                key={org.id || org.organizationId}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleOrganizationClick(org)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={org.photoUrl || org.profilePictureUrl || defaultProfilePhoto}
                      alt={org.name || org.organizationName}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{org.name || org.organizationName}</h4>
                      </div>
                      
                      {(org.username || org.userName) && (
                        <p className={styles.actUsername}>@{org.username || org.userName}</p>
                      )}
                      
                      {(org.role || org.organizationType) && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faBuilding} />
                          <span>{org.role || org.organizationType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveOrganization(org.id || org.organizationId, org.name || org.organizationName);
                        }}
                        title="Remove organization"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMore && (
              <button 
                className={styles.loadMoreButton}
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No organizations yet.</p>
            {isOwnProfile && (
              <p className={styles.emptyStateMessage}>
                Organizations will appear here when you join as staff or are added to an organization.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };
  
// Updated StaffSection with proper data fetching and delete functionality
const StaffSection = ({ profileData, isOwnProfile, showCustomAlert, navigate }) => {
  const [activeTab, setActiveTab] = useState('current');
  const [currentStaff, setCurrentStaff] = useState([]);
  const [pastStaff, setPastStaff] = useState([]);
  const [displayedCurrentStaff, setDisplayedCurrentStaff] = useState([]);
  const [displayedPastStaff, setDisplayedPastStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  const { userData } = useContext(UserContext);
  
  // Add a ref to track if data was loaded
  const dataLoadedRef = useRef(false);
  
  // Refresh function that can be called programmatically
  const refreshStaffData = useCallback(async () => {
    if (!profileData?.userId) return;
    fetchStaff(profileData.userId);
  }, [profileData?.userId]);
  
  // Listen for userData changes and refresh when needed
  useEffect(() => {
    if (profileData?.userId && userData && isOwnProfile) {
      // If we've already loaded data once, then refresh when userData changes
      if (dataLoadedRef.current) {
        refreshStaffData();
      }
    }
  }, [userData, profileData?.userId, isOwnProfile, refreshStaffData]);
  
  // Initial data load
  useEffect(() => {
    if (profileData?.userId) {
      fetchStaff(profileData.userId);
      dataLoadedRef.current = true;
    }
  }, [profileData?.userId]);
  
  const fetchStaff = async (userId) => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // First get the user document to get staff count and top 3 staff
      const userDoc = await getDoc(doc(db, 'users', userId));
      let initialCurrentStaff = [];
      let initialPastStaff = [];
      let currentStaffCount = 0;
      let pastStaffCount = 0;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        initialCurrentStaff = userData.vendorStaff || [];
        initialPastStaff = userData.pastVendorStaff || [];
        currentStaffCount = userData.vendorStaffCount || 0;
        pastStaffCount = userData.pastVendorStaffCount || 0;
      }
      
      // Set initial displayed staff
      setDisplayedCurrentStaff(initialCurrentStaff);
      setDisplayedPastStaff(initialPastStaff);
      
      // Check if there are more staff than what's displayed
      setHasMoreCurrent(currentStaffCount > initialCurrentStaff.length);
      setHasMorePast(pastStaffCount > initialPastStaff.length);
      
      // Fetch all current staff from subcollection
      if (currentStaffCount > 0) {
        const staffRef = collection(db, 'users', userId, 'vendorStaff');
        const currentQuery = query(staffRef, where('isPast', '==', false));
        const currentSnapshot = await getDocs(currentQuery);
        
        if (!currentSnapshot.empty) {
          const allCurrentStaff = await Promise.all(
            currentSnapshot.docs.map(async (docSnapshot) => {
              const staffData = docSnapshot.data();
              
              // Enrich with staff user data if needed
              if (staffData.userId && (!staffData.fullName || !staffData.userName)) {
                try {
                  const staffUserDoc = await getDoc(doc(db, 'users', staffData.userId));
                  if (staffUserDoc.exists()) {
                    const staffUserData = staffUserDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...staffData,
                      fullName: staffUserData.fullName || staffData.fullName || '',
                      userName: staffUserData.userName || staffData.userName || '',
                      profilePictureUrl: staffUserData.profilePictureUrl || staffData.profilePictureUrl || '',
                      userSubType: staffUserData.userSubType || staffData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching staff user data for ${staffData.userId}:`, error);
                }
              }
              
              // Return the staff data as is if user data enrichment failed
              return {
                id: docSnapshot.id,
                ...staffData
              };
            })
          );
          
          // Store all current staff
          setCurrentStaff(allCurrentStaff);
        } else {
          setCurrentStaff(initialCurrentStaff);
        }
      }
      
      // Fetch all past staff from subcollection
      if (pastStaffCount > 0) {
        const staffRef = collection(db, 'users', userId, 'vendorStaff');
        const pastQuery = query(staffRef, where('isPast', '==', true));
        const pastSnapshot = await getDocs(pastQuery);
        
        if (!pastSnapshot.empty) {
          const allPastStaff = await Promise.all(
            pastSnapshot.docs.map(async (docSnapshot) => {
              const staffData = docSnapshot.data();
              
              // Enrich with staff user data if needed
              if (staffData.userId && (!staffData.fullName || !staffData.userName)) {
                try {
                  const staffUserDoc = await getDoc(doc(db, 'users', staffData.userId));
                  if (staffUserDoc.exists()) {
                    const staffUserData = staffUserDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...staffData,
                      fullName: staffUserData.fullName || staffData.fullName || '',
                      userName: staffUserData.userName || staffData.userName || '',
                      profilePictureUrl: staffUserData.profilePictureUrl || staffData.profilePictureUrl || '',
                      userSubType: staffUserData.userSubType || staffData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching staff user data for ${staffData.userId}:`, error);
                }
              }
              
              // Return the staff data as is if user data enrichment failed
              return {
                id: docSnapshot.id,
                ...staffData
              };
            })
          );
          
          // Store all past staff
          setPastStaff(allPastStaff);
        } else {
          setPastStaff(initialPastStaff);
        }
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load staff',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStaffClick = (member) => {
    if (member.userName) {
      navigate(`/${member.userName}`);
    } else if (member.userId) {
      // Navigate to staff profile by ID
      navigate(`/users/${member.userId}`);
    }
  };
  
  const handleLoadMore = async (isPast = false) => {
    if (loadingMore || (!hasMoreCurrent && !isPast) || (!hasMorePast && isPast) || !profileData?.userId) return;
    
    setLoadingMore(true);
    try {
      if (isPast) {
        // Load more past staff from the full list
        const nextBatch = pastStaff.slice(
          displayedPastStaff.length,
          displayedPastStaff.length + 3
        );
        
        setDisplayedPastStaff(prev => [...prev, ...nextBatch]);
        
        // Check if there are more past staff to load
        setHasMorePast(displayedPastStaff.length + nextBatch.length < pastStaff.length);
      } else {
        // Load more current staff from the full list
        const nextBatch = currentStaff.slice(
          displayedCurrentStaff.length,
          displayedCurrentStaff.length + 3
        );
        
        setDisplayedCurrentStaff(prev => [...prev, ...nextBatch]);
        
        // Check if there are more current staff to load
        setHasMoreCurrent(displayedCurrentStaff.length + nextBatch.length < currentStaff.length);
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} staff:`, error);
      showCustomAlert({
        title: 'Error',
        message: `Failed to load more ${isPast ? 'past' : 'current'} staff`,
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };
  
  const handleRemoveStaff = async (staff, isPast = false) => {
    if (!window.confirm(`Are you sure you want to remove ${staff.fullName || 'this staff member'}?`)) {
      return;
    }
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', profileData.userId);
      
      // 1. Remove from subcollection
      const staffRef = doc(db, 'users', profileData.userId, 'vendorStaff', staff.id);
      await deleteDoc(staffRef);
      
      // 2. Update the main user document
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // 3. Get new top 3 staff for main document
        const staffColRef = collection(db, 'users', profileData.userId, 'vendorStaff');
        const top3Query = query(
          staffColRef,
          where('isPast', '==', isPast),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const top3Snapshot = await getDocs(top3Query);
        const top3Staff = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const staffData = docSnapshot.data();
            
            // Enrich with user data
            if (staffData.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...staffData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${staffData.userId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...staffData
            };
          })
        );
        
        // 4. Update main document with top3 and the count
        if (isPast) {
          await updateDoc(userRef, {
            pastVendorStaff: top3Staff,
            pastVendorStaffCount: Math.max(0, (userData.pastVendorStaffCount || 0) - 1)
          });
        } else {
          await updateDoc(userRef, {
            vendorStaff: top3Staff,
            vendorStaffCount: Math.max(0, (userData.vendorStaffCount || 0) - 1)
          });
        }
        
        // 5. Update staff's associatedVendors collection
        if (staff.otherSideDocId) {
          try {
            await deleteDoc(doc(db, 'users', staff.userId, 'associatedVendors', staff.otherSideDocId));
            
            // Update the staff's main document with associatedVendors count
            const staffUserRef = doc(db, 'users', staff.userId);
            const staffUserDoc = await getDoc(staffUserRef);
            
            if (staffUserDoc.exists()) {
              const staffUserData = staffUserDoc.data();
              
              // Get new top 3 associated vendors
              const associatedVendorsRef = collection(db, 'users', staff.userId, 'associatedVendors');
              const top3VendorsQuery = query(
                associatedVendorsRef,
                where('isPast', '==', isPast),
                orderBy('createdAt', 'desc'),
                limit(3)
              );
              
              const top3VendorsSnapshot = await getDocs(top3VendorsQuery);
              const top3Vendors = await Promise.all(
                top3VendorsSnapshot.docs.map(async (docSnapshot) => {
                  const vendorData = docSnapshot.data();
                  
                  // Enrich with vendor data
                  if (vendorData.vendorId) {
                    try {
                      const vendorDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                      if (vendorDoc.exists()) {
                        const vendorUserData = vendorDoc.data();
                        return {
                          id: docSnapshot.id,
                          ...vendorData,
                          fullName: vendorUserData.fullName || '',
                          userName: vendorUserData.userName || '',
                          profilePictureUrl: vendorUserData.profilePictureUrl || '',
                          userSubType: vendorUserData.userSubType || ''
                        };
                      }
                    } catch (error) {
                      console.error(`Error fetching vendor data for ${vendorData.vendorId}:`, error);
                    }
                  }
                  
                  return {
                    id: docSnapshot.id,
                    ...vendorData
                  };
                })
              );
              
              // Update staff's document
              const countField = isPast ? 'pastAssociatedVendorsCount' : 'associatedVendorsCount';
              const vendorsField = isPast ? 'pastAssociatedVendors' : 'associatedVendors';
              
              await updateDoc(staffUserRef, {
                [vendorsField]: top3Vendors,
                [countField]: increment(-1)
              });
            }
          } catch (e) {
            console.error('Error updating staff associatedVendors:', e);
            // Continue even if this fails
          }
        }
        
        // 6. Create removal notification for the staff
        await createNotification(staff.userId, 'general', {
          type: isPast ? NOTIFICATION_TYPES.PAST_VENDOR_STAFF_REMOVAL : NOTIFICATION_TYPES.VENDOR_STAFF_REMOVAL,
          title: `${isPast ? 'Past ' : ''}Staff Relationship Removed`,
          message: `${userData.fullName || 'A vendor'} has removed you from their ${isPast ? 'past ' : ''}staff.`,
          vendorId: profileData.userId,
          status: 'removed',
          timestamp: serverTimestamp(),
          data: {
            _displayData: {
              vendorName: userData.fullName || '',
              vendorType: userData.userSubType || ''
            }
          }
        });
        
        // 7. Update UI state
        if (isPast) {
          const updatedPastStaff = pastStaff.filter(s => s.id !== staff.id);
          setPastStaff(updatedPastStaff);
          setDisplayedPastStaff(displayedPastStaff.filter(s => s.id !== staff.id));
          setHasMorePast((userData.pastVendorStaffCount || 0) - 1 > top3Staff.length);
        } else {
          const updatedCurrentStaff = currentStaff.filter(s => s.id !== staff.id);
          setCurrentStaff(updatedCurrentStaff);
          setDisplayedCurrentStaff(displayedCurrentStaff.filter(s => s.id !== staff.id));
          setHasMoreCurrent((userData.vendorStaffCount || 0) - 1 > top3Staff.length);
        }
        
        showCustomAlert({
          title: 'Success',
          message: `Successfully removed ${staff.fullName || 'staff member'} from ${isPast ? 'past' : 'current'} staff`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error removing staff:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to remove staff',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    
    // Get month names
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const startMonthName = months[parseInt(startMonth) - 1] || '';
    const endMonthName = months[parseInt(endMonth) - 1] || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };
  
  // Format since for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    
    // Get month names
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthName = months[parseInt(month) - 1] || '';
    
    return `Since ${monthName} ${year}`;
  };
  
  // Render permissions badges
  const renderPermissionBadges = (permissions) => {
    if (!permissions) return null;
    
    const badges = [];
    
    if (permissions.canAddItems) badges.push('Add Items');
    if (permissions.canEditItems) badges.push('Edit Items');
    if (permissions.canDeleteItems) badges.push('Delete Items');
    if (permissions.canChangeStatus) badges.push('Change Status');
    
    if (badges.length === 0) return null;
    
    return (
      <div className={styles.permissionBadges}>
        {badges.map((badge, index) => (
          <span key={index} className={styles.permissionBadge}>{badge}</span>
        ))}
      </div>
    );
  };
  
  return (
    <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
      <h3 className={styles.sectionTitle}>Staff</h3>
      
      {/* Tabs for Current/Past */}
      <div className={styles.actsTabs}>
        <button 
          className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading {activeTab} staff...</p>
        </div>
      ) : activeTab === 'current' ? (
        displayedCurrentStaff.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedCurrentStaff.map(member => (
              <div 
                key={member.id || member.userId}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleStaffClick(member)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={member.profilePictureUrl || defaultProfilePhoto}
                      alt={member.fullName}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{member.fullName}</h4>
                        {member.idVerificationStatus === 'Verified' && (
                          <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                        )}
                      </div>
                      
                      {member.userName && (
                        <p className={styles.actUsername}>@{member.userName}</p>
                      )}
                      
                      {member.userSubType && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserShield} />
                          <span>{member.userSubType}</span>
                        </div>
                      )}
                      
                      {/* Display permissions badges */}
                      {renderPermissionBadges(member.permissions)}
                      
                      {member.since && (
                        <div className={styles.sinceBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{formatSince(member.since)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveStaff(member, false);
                        }}
                        title="Remove staff"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMoreCurrent && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(false)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No current staff members.</p>
            {isOwnProfile && (
              <p className={styles.emptyStateMessage}>
                Staff will appear here when you add staff members to your organization.
              </p>
            )}
          </div>
        )
      ) : (
        // Past staff
        displayedPastStaff.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedPastStaff.map(member => (
              <div 
                key={member.id || `past-${member.userId}`}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleStaffClick(member)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={member.profilePictureUrl || defaultProfilePhoto}
                      alt={member.fullName}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{member.fullName}</h4>
                        {member.idVerificationStatus === 'Verified' && (
                          <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                        )}
                      </div>
                      
                      {member.userName && (
                        <p className={styles.actUsername}>@{member.userName}</p>
                      )}
                      
                      {member.userSubType && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserShield} />
                          <span>{member.userSubType}</span>
                        </div>
                      )}
                      
                      {/* Display permissions badges */}
                      {renderPermissionBadges(member.permissions)}
                      
                      {member.duration && (
                        <div className={styles.durationBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{formatDuration(member.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveStaff(member, true);
                        }}
                        title="Remove staff"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMorePast && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(true)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No past staff members.</p>
            {isOwnProfile && (
              <p className={styles.emptyStateMessage}>
                Staff will appear here when you add past staff members to your organization.
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
};
  
  // New Component for Associated Vendors Section
// Updated AssociatedVendorsSection with proper data fetching, tabs, and delete functionality
const AssociatedVendorsSection = ({ profileData, isOwnProfile, showCustomAlert, navigate }) => {
  const [activeTab, setActiveTab] = useState('current');
  const [currentVendors, setCurrentVendors] = useState([]);
  const [pastVendors, setPastVendors] = useState([]);
  const [displayedCurrentVendors, setDisplayedCurrentVendors] = useState([]);
  const [displayedPastVendors, setDisplayedPastVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  const { userData } = useContext(UserContext);
  
  // Add a ref to track if data was loaded
  const dataLoadedRef = useRef(false);
  
  // Refresh function that can be called programmatically
  const refreshVendorData = useCallback(async () => {
    if (!profileData?.userId) return;
    fetchAssociatedVendors(profileData.userId);
  }, [profileData?.userId]);
  
  // Listen for userData changes and refresh when needed
  useEffect(() => {
    if (profileData?.userId && userData && isOwnProfile) {
      // If we've already loaded data once, then refresh when userData changes
      if (dataLoadedRef.current) {
        refreshVendorData();
      }
    }
  }, [userData, profileData?.userId, isOwnProfile, refreshVendorData]);
  
  // Initial data load
  useEffect(() => {
    if (profileData?.userId) {
      fetchAssociatedVendors(profileData.userId);
      dataLoadedRef.current = true;
    }
  }, [profileData?.userId]);
  
  const fetchAssociatedVendors = async (userId) => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // First get the user document to get vendor count and top 3 vendors
      const userDoc = await getDoc(doc(db, 'users', userId));
      let initialCurrentVendors = [];
      let initialPastVendors = [];
      let currentCount = 0;
      let pastCount = 0;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        initialCurrentVendors = userData.associatedVendors || [];
        initialPastVendors = userData.pastAssociatedVendors || [];
        currentCount = userData.associatedVendorsCount || 0;
        pastCount = userData.pastAssociatedVendorsCount || 0;
        
        // Enrich current vendors with additional data if needed
        if (initialCurrentVendors.length > 0) {
          initialCurrentVendors = await Promise.all(
            initialCurrentVendors.map(async (vendor) => {
              // Make sure we're using vendorId (not userId) for vendor references
              const vendorId = vendor.vendorId || vendor.userId;
              
              if (vendorId && (!vendor.fullName || !vendor.userName)) {
                try {
                  const vendorDoc = await getDoc(doc(db, 'users', vendorId));
                  if (vendorDoc.exists()) {
                    const vendorData = vendorDoc.data();
                    return {
                      ...vendor,
                      vendorId: vendorId, // Ensure consistent field naming
                      fullName: vendorData.fullName || vendor.name || '',
                      userName: vendorData.userName || vendor.username || '',
                      profilePictureUrl: vendorData.profilePictureUrl || vendor.photoUrl || '',
                      userSubType: vendorData.userSubType || vendor.subType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching vendor data for ${vendorId}:`, error);
                }
              }
              return {
                ...vendor,
                // Normalize field names
                vendorId: vendorId,
                fullName: vendor.fullName || vendor.name || '',
                userName: vendor.userName || vendor.username || '',
                profilePictureUrl: vendor.profilePictureUrl || vendor.photoUrl || '',
                userSubType: vendor.userSubType || vendor.subType || ''
              };
            })
          );
        }
        
        // Enrich past vendors with additional data if needed
        if (initialPastVendors.length > 0) {
          initialPastVendors = await Promise.all(
            initialPastVendors.map(async (vendor) => {
              // Make sure we're using vendorId (not userId) for vendor references
              const vendorId = vendor.vendorId || vendor.userId;
              
              if (vendorId && (!vendor.fullName || !vendor.userName)) {
                try {
                  const vendorDoc = await getDoc(doc(db, 'users', vendorId));
                  if (vendorDoc.exists()) {
                    const vendorData = vendorDoc.data();
                    return {
                      ...vendor,
                      vendorId: vendorId, // Ensure consistent field naming
                      fullName: vendorData.fullName || vendor.name || '',
                      userName: vendorData.userName || vendor.username || '',
                      profilePictureUrl: vendorData.profilePictureUrl || vendor.photoUrl || '',
                      userSubType: vendorData.userSubType || vendor.subType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching past vendor data for ${vendorId}:`, error);
                }
              }
              return {
                ...vendor,
                // Normalize field names
                vendorId: vendorId,
                fullName: vendor.fullName || vendor.name || '',
                userName: vendor.userName || vendor.username || '',
                profilePictureUrl: vendor.profilePictureUrl || vendor.photoUrl || '',
                userSubType: vendor.userSubType || vendor.subType || ''
              };
            })
          );
        }
      }
      
      // Set initial displayed vendors
      setDisplayedCurrentVendors(initialCurrentVendors);
      setDisplayedPastVendors(initialPastVendors);
      
      // Check if there are more vendors than what's displayed
      setHasMoreCurrent(currentCount > initialCurrentVendors.length);
      setHasMorePast(pastCount > initialPastVendors.length);
      
      // Fetch all current vendors from subcollection
      if (currentCount > 0) {
        const vendorsRef = collection(db, 'users', userId, 'associatedVendors');
        const currentQuery = query(vendorsRef, where('isPast', '==', false));
        const currentSnapshot = await getDocs(currentQuery);
        
        if (!currentSnapshot.empty) {
          const allCurrentVendors = await Promise.all(
            currentSnapshot.docs.map(async (docSnapshot) => {
              const vendorData = docSnapshot.data();
              
              // Enrich with vendor data
              const vendorId = vendorData.vendorId || vendorData.userId;
              
              if (vendorId) {
                try {
                  const vendorDoc = await getDoc(doc(db, 'users', vendorId));
                  if (vendorDoc.exists()) {
                    const userData = vendorDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...vendorData,
                      vendorId: vendorId,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || '',
                      permissions: vendorData.permissions || {}
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${vendorId}:`, error);
                }
              }
              
              // Return the vendor data as is if enrichment failed
              return {
                id: docSnapshot.id,
                ...vendorData,
                vendorId: vendorId,
                permissions: vendorData.permissions || {}
              };
            })
          );
          
          setCurrentVendors(allCurrentVendors);
        } else {
          setCurrentVendors(initialCurrentVendors);
        }
      }
      
      // Fetch all past vendors from subcollection
      if (pastCount > 0) {
        const vendorsRef = collection(db, 'users', userId, 'associatedVendors');
        const pastQuery = query(vendorsRef, where('isPast', '==', true));
        const pastSnapshot = await getDocs(pastQuery);
        
        if (!pastSnapshot.empty) {
          const allPastVendors = await Promise.all(
            pastSnapshot.docs.map(async (docSnapshot) => {
              const vendorData = docSnapshot.data();
              
              // Enrich with vendor data
              const vendorId = vendorData.vendorId || vendorData.userId;
              
              if (vendorId) {
                try {
                  const vendorDoc = await getDoc(doc(db, 'users', vendorId));
                  if (vendorDoc.exists()) {
                    const userData = vendorDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...vendorData,
                      vendorId: vendorId,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || '',
                      permissions: vendorData.permissions || {}
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${vendorId}:`, error);
                }
              }
              
              // Return the vendor data as is if enrichment failed
              return {
                id: docSnapshot.id,
                ...vendorData,
                vendorId: vendorId,
                permissions: vendorData.permissions || {}
              };
            })
          );
          
          setPastVendors(allPastVendors);
        } else {
          setPastVendors(initialPastVendors);
        }
      }
    } catch (error) {
      console.error('Error fetching associated vendors:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load associated vendors',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVendorClick = (vendor) => {
    if (vendor.userName) {
      navigate(`/${vendor.userName}`);
    } else if (vendor.vendorId) {
      navigate(`/users/${vendor.vendorId}`);
    }
  };
  
  const handleLoadMore = async (isPast = false) => {
    if (loadingMore || (!hasMoreCurrent && !isPast) || (!hasMorePast && isPast) || !profileData?.userId) return;
    
    setLoadingMore(true);
    try {
      if (isPast) {
        // Load more past vendors from the full list
        const nextBatch = pastVendors.slice(
          displayedPastVendors.length,
          displayedPastVendors.length + 3
        );
        
        setDisplayedPastVendors(prev => [...prev, ...nextBatch]);
        
        // Check if there are more past vendors to load
        setHasMorePast(displayedPastVendors.length + nextBatch.length < pastVendors.length);
      } else {
        // Load more current vendors from the full list
        const nextBatch = currentVendors.slice(
          displayedCurrentVendors.length,
          displayedCurrentVendors.length + 3
        );
        
        setDisplayedCurrentVendors(prev => [...prev, ...nextBatch]);
        
        // Check if there are more current vendors to load
        setHasMoreCurrent(displayedCurrentVendors.length + nextBatch.length < currentVendors.length);
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} vendors:`, error);
      showCustomAlert({
        title: 'Error',
        message: `Failed to load more ${isPast ? 'past' : 'current'} vendors`,
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };
  
  const handleRemoveVendor = async (vendor, isPast = false) => {
    if (!window.confirm(`Are you sure you want to remove ${vendor.fullName || 'this vendor'}?`)) {
      return;
    }
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', profileData.userId);
      
      // 1. Remove from subcollection
      const vendorRef = doc(db, 'users', profileData.userId, 'associatedVendors', vendor.id);
      await deleteDoc(vendorRef);
      
      // 2. Update the main user document
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // 3. Get new top 3 vendors for main document
        const vendorsRef = collection(db, 'users', profileData.userId, 'associatedVendors');
        const top3Query = query(
          vendorsRef,
          where('isPast', '==', isPast),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const top3Snapshot = await getDocs(top3Query);
        const top3Vendors = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const vendorData = docSnapshot.data();
            const vendorId = vendorData.vendorId || vendorData.userId;
            
            // Enrich with vendor data
            if (vendorId) {
              try {
                const vendorDoc = await getDoc(doc(db, 'users', vendorId));
                if (vendorDoc.exists()) {
                  const userData = vendorDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...vendorData,
                    vendorId: vendorId,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${vendorId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...vendorData
            };
          })
        );
        
        // 4. Update main document with top3 and the count
        if (isPast) {
          await updateDoc(userRef, {
            pastAssociatedVendors: top3Vendors,
            pastAssociatedVendorsCount: Math.max(0, (userData.pastAssociatedVendorsCount || 0) - 1)
          });
        } else {
          await updateDoc(userRef, {
            associatedVendors: top3Vendors,
            associatedVendorsCount: Math.max(0, (userData.associatedVendorsCount || 0) - 1)
          });
        }
        
        // 5. Also update the vendor's vendorStaff collection
        if (vendor.vendorId) {
          try {
            // Find the related document in vendor's vendorStaff collection
            const vendorStaffQuery = query(
              collection(db, 'users', vendor.vendorId, 'vendorStaff'),
              where('userId', '==', profileData.userId)
            );
            const vendorStaffSnapshot = await getDocs(vendorStaffQuery);
            
            if (!vendorStaffSnapshot.empty) {
              // Remove all matching entries
              for (const doc of vendorStaffSnapshot.docs) {
                await deleteDoc(doc.ref);
              }
              
              // Update the vendor's main document
              const vendorRef = doc(db, 'users', vendor.vendorId);
              const vendorDoc = await getDoc(vendorRef);
              
              if (vendorDoc.exists()) {
                const vendorData = vendorDoc.data();
                
                // Get new top 3 staff 
                const vendorStaffRef = collection(db, 'users', vendor.vendorId, 'vendorStaff');
                const top3StaffQuery = query(
                  vendorStaffRef,
                  where('isPast', '==', isPast),
                  orderBy('createdAt', 'desc'),
                  limit(3)
                );
                
                const top3StaffSnapshot = await getDocs(top3StaffQuery);
                const top3Staff = await Promise.all(
                  top3StaffSnapshot.docs.map(async (docSnapshot) => {
                    const staffData = docSnapshot.data();
                    
                    // Enrich with user data
                    if (staffData.userId) {
                      try {
                        const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                        if (userDoc.exists()) {
                          const userData = userDoc.data();
                          return {
                            id: docSnapshot.id,
                            ...staffData,
                            fullName: userData.fullName || '',
                            userName: userData.userName || '',
                            profilePictureUrl: userData.profilePictureUrl || '',
                            userSubType: userData.userSubType || '',
                            idVerificationStatus: userData.idVerificationStatus || ''
                          };
                        }
                      } catch (error) {
                        console.error(`Error fetching user data for ${staffData.userId}:`, error);
                      }
                    }
                    
                    return {
                      id: docSnapshot.id,
                      ...staffData
                    };
                  })
                );
                
                // Update vendor's document with staff count
                const countField = isPast ? 'pastVendorStaffCount' : 'vendorStaffCount';
                const staffField = isPast ? 'pastVendorStaff' : 'vendorStaff';
                
                await updateDoc(vendorRef, {
                  [staffField]: top3Staff,
                  [countField]: increment(-1) 
                });
              }
            }
          } catch (e) {
            console.error('Error updating vendor document:', e);
            // Continue even if this fails
          }
          
          // Create notification for the vendor
          try {
            await createNotification(vendor.vendorId, 'general', {
              type: NOTIFICATION_TYPES.VENDOR_STAFF_REMOVAL,
              title: `${isPast ? 'Past ' : ''}Staff Relationship Removed`,
              message: `${userData.fullName || 'A user'} has removed you from their ${isPast ? 'past ' : ''}associated vendors.`,
              userId: profileData.userId,
              status: 'removed',
              timestamp: serverTimestamp(),
              data: {
                _displayData: {
                  userName: userData.userName || '',
                  fullName: userData.fullName || '',
                  profilePictureUrl: userData.profilePictureUrl || ''
                }
              }
            });
          } catch (notifError) {
            console.error('Error sending notification:', notifError);
          }
        }
        
        // 6. Update UI state
        if (isPast) {
          const updatedPastVendors = pastVendors.filter(v => v.id !== vendor.id);
          setPastVendors(updatedPastVendors);
          setDisplayedPastVendors(displayedPastVendors.filter(v => v.id !== vendor.id));
          setHasMorePast((userData.pastAssociatedVendorsCount || 0) - 1 > top3Vendors.length);
        } else {
          const updatedCurrentVendors = currentVendors.filter(v => v.id !== vendor.id);
          setCurrentVendors(updatedCurrentVendors);
          setDisplayedCurrentVendors(displayedCurrentVendors.filter(v => v.id !== vendor.id));
          setHasMoreCurrent((userData.associatedVendorsCount || 0) - 1 > top3Vendors.length);
        }
        
        showCustomAlert({
          title: 'Success',
          message: `Successfully removed ${vendor.fullName || 'vendor'} from ${isPast ? 'past' : 'current'} associated vendors`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error removing associated vendor:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to remove associated vendor',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    
    // Get month names
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const startMonthName = months[parseInt(startMonth) - 1] || '';
    const endMonthName = months[parseInt(endMonth) - 1] || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };
  
  // Format since for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    
    // Get month names
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthName = months[parseInt(month) - 1] || '';
    
    return `Since ${monthName} ${year}`;
  };
  
  // Render permissions badges
  const renderPermissionBadges = (permissions) => {
    if (!permissions) return null;
    
    const badges = [];
    
    if (permissions.canAddItems) badges.push('Add Items');
    if (permissions.canEditItems) badges.push('Edit Items');
    if (permissions.canDeleteItems) badges.push('Delete Items');
    if (permissions.canChangeStatus) badges.push('Change Status');
    
    if (badges.length === 0) return null;
    
    return (
      <div className={styles.permissionBadges}>
        {badges.map((badge, index) => (
          <span key={index} className={styles.permissionBadge}>{badge}</span>
        ))}
      </div>
    );
  };
  
  // Navigate to search page for adding vendors
  const handleAddVendor = () => {
    // Could navigate to a search page for vendors
    navigate('/associated-vendors');
  };
  
  return (
    <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
      <h3 className={styles.sectionTitle}>Associated Vendors</h3>
      
      {/* Tabs for Current/Past */}
      <div className={styles.actsTabs}>
        <button 
          className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      {isOwnProfile && (
        <div className={styles.buttonContainer}>
          <button 
            className={styles.actionButton}
            onClick={handleAddVendor}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add {activeTab === 'past' ? 'Past ' : ''}Vendor
          </button>
        </div>
      )}
      
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading {activeTab} associated vendors...</p>
        </div>
      ) : activeTab === 'current' ? (
        displayedCurrentVendors.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedCurrentVendors.map(vendor => (
              <div 
                key={vendor.id || `${vendor.vendorId}`}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleVendorClick(vendor)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={vendor.profilePictureUrl || defaultProfilePhoto}
                      alt={vendor.fullName}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{vendor.fullName}</h4>
                      </div>
                      
                      {vendor.userName && (
                        <p className={styles.actUsername}>@{vendor.userName}</p>
                      )}
                      
                      {vendor.userSubType && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faStore} />
                          <span>{vendor.userSubType}</span>
                        </div>
                      )}
                      
                      {/* Display permissions badges */}
                      {renderPermissionBadges(vendor.permissions)}
                      
                      {vendor.since && (
                        <div className={styles.sinceBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{formatSince(vendor.since)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVendor(vendor, false);
                        }}
                        title="Remove vendor"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMoreCurrent && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(false)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No current associated vendors.</p>
            {isOwnProfile && (
              <p className={styles.emptyStateMessage}>
                Add vendors you're currently working with.
              </p>
            )}
          </div>
        )
      ) : (
        // Past vendors
        displayedPastVendors.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedPastVendors.map(vendor => (
              <div 
                key={vendor.id || `past-${vendor.vendorId}`}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleVendorClick(vendor)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={vendor.profilePictureUrl || defaultProfilePhoto}
                      alt={vendor.fullName}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{vendor.fullName}</h4>
                      </div>
                      
                      {vendor.userName && (
                        <p className={styles.actUsername}>@{vendor.userName}</p>
                      )}
                      
                      {vendor.userSubType && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faStore} />
                          <span>{vendor.userSubType}</span>
                        </div>
                      )}
                      
                      {/* Display permissions badges */}
                      {renderPermissionBadges(vendor.permissions)}
                      
                      {vendor.duration && (
                        <div className={styles.durationBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{formatDuration(vendor.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVendor(vendor, true);
                        }}
                        title="Remove vendor"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMorePast && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(true)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No past associated vendors.</p>
            {isOwnProfile && (
              <p className={styles.emptyStateMessage}>
                Add vendors you've worked with in the past.
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
};
  
  // New Component for Managing Acts Section
// Updated ManagingActsSection with proper data fetching, tabs, and delete functionality
const ManagingActsSection = ({ profileData, isOwnProfile, showCustomAlert, navigate }) => {
  const [activeTab, setActiveTab] = useState('current');
  const [currentActs, setCurrentActs] = useState([]);
  const [pastActs, setPastActs] = useState([]);
  const [displayedCurrentActs, setDisplayedCurrentActs] = useState([]);
  const [displayedPastActs, setDisplayedPastActs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  const { userData } = useContext(UserContext);
  
  useEffect(() => {
    if (profileData?.userId) {
      fetchManagingActs(profileData.userId);
    }
  }, [profileData?.userId]);
  
  const fetchManagingActs = async (userId) => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // First get the user document to get managing acts count and top 3 entries
      const userDoc = await getDoc(doc(db, 'users', userId));
      let initialCurrentActs = [];
      let initialPastActs = [];
      let currentCount = 0;
      let pastCount = 0;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        initialCurrentActs = userData.managingActs || [];
        initialPastActs = userData.pastManagingActs || [];
        currentCount = userData.managingActsCount || 0;
        pastCount = userData.pastManagingActsCount || 0;
        
        // Enrich current acts with additional data if needed
        if (initialCurrentActs.length > 0) {
          initialCurrentActs = await Promise.all(
            initialCurrentActs.map(async (act) => {
              if (act.userId && (!act.name || !act.username)) {
                try {
                  const actDoc = await getDoc(doc(db, 'users', act.userId));
                  if (actDoc.exists()) {
                    const actData = actDoc.data();
                    return {
                      ...act,
                      name: actData.fullName || '',
                      username: actData.userName || '',
                      photoUrl: actData.profilePictureUrl || '',
                      subType: actData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching act data for ${act.userId}:`, error);
                }
              } else if (act.type === 'band' && act.id && (!act.name || !act.username)) {
                try {
                  const bandDoc = await getDoc(doc(db, 'bands', act.id));
                  if (bandDoc.exists()) {
                    const bandData = bandDoc.data();
                    return {
                      ...act,
                      name: bandData.bandFullName || '',
                      username: bandData.bandUserName || '',
                      photoUrl: bandData.bandProfilePictureUrl || '',
                      genre: bandData.bandGenres?.join(', ') || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching band data for ${act.id}:`, error);
                }
              }
              return act;
            })
          );
        }
        
        // Enrich past acts with additional data if needed
        if (initialPastActs.length > 0) {
          initialPastActs = await Promise.all(
            initialPastActs.map(async (act) => {
              if (act.userId && (!act.name || !act.username)) {
                try {
                  const actDoc = await getDoc(doc(db, 'users', act.userId));
                  if (actDoc.exists()) {
                    const actData = actDoc.data();
                    return {
                      ...act,
                      name: actData.fullName || '',
                      username: actData.userName || '',
                      photoUrl: actData.profilePictureUrl || '',
                      subType: actData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching past act data for ${act.userId}:`, error);
                }
              } else if (act.type === 'band' && act.id && (!act.name || !act.username)) {
                try {
                  const bandDoc = await getDoc(doc(db, 'bands', act.id));
                  if (bandDoc.exists()) {
                    const bandData = bandDoc.data();
                    return {
                      ...act,
                      name: bandData.bandFullName || '',
                      username: bandData.bandUserName || '',
                      photoUrl: bandData.bandProfilePictureUrl || '',
                      genre: bandData.bandGenres?.join(', ') || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching past band data for ${act.id}:`, error);
                }
              }
              return act;
            })
          );
        }
      }
      
      // Set initial displayed acts
      setDisplayedCurrentActs(initialCurrentActs);
      setDisplayedPastActs(initialPastActs);
      
      // Check if there are more acts than what's displayed
      setHasMoreCurrent(currentCount > initialCurrentActs.length);
      setHasMorePast(pastCount > initialPastActs.length);
      
      // Fetch all current acts from subcollection
      if (initialCurrentActs.length > 0) {
        const currentActsRef = collection(db, 'users', userId, 'managingActs');
        const currentQuery = query(currentActsRef, where('isPast', '==', false));
        const currentSnapshot = await getDocs(currentQuery);
        
        if (!currentSnapshot.empty) {
          const allCurrentActs = await Promise.all(
            currentSnapshot.docs.map(async (docSnapshot) => {
              const actData = docSnapshot.data();
              
              // Enrich with act data
              if (actData.userId) {
                try {
                  const actDoc = await getDoc(doc(db, 'users', actData.userId));
                  if (actDoc.exists()) {
                    const userData = actDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...actData,
                      name: userData.fullName || '',
                      username: userData.userName || '',
                      photoUrl: userData.profilePictureUrl || '',
                      subType: userData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${actData.userId}:`, error);
                }
              } else if (actData.type === 'band' && actData.id) {
                try {
                  const bandDoc = await getDoc(doc(db, 'bands', actData.id));
                  if (bandDoc.exists()) {
                    const bandData = bandDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...actData,
                      name: bandData.bandFullName || '',
                      username: bandData.bandUserName || '',
                      photoUrl: bandData.bandProfilePictureUrl || '',
                      genre: bandData.bandGenres?.join(', ') || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching band data for ${actData.id}:`, error);
                }
              }
              
              // Return the act data as is if enrichment failed
              return {
                id: docSnapshot.id,
                ...actData
              };
            })
          );
          
          setCurrentActs(allCurrentActs);
        } else {
          setCurrentActs(initialCurrentActs);
        }
      }
      
      // Fetch all past acts from subcollection
      if (initialPastActs.length > 0) {
        const pastActsRef = collection(db, 'users', userId, 'managingActs');
        const pastQuery = query(pastActsRef, where('isPast', '==', true));
        const pastSnapshot = await getDocs(pastQuery);
        
        if (!pastSnapshot.empty) {
          const allPastActs = await Promise.all(
            pastSnapshot.docs.map(async (docSnapshot) => {
              const actData = docSnapshot.data();
              
              // Enrich with act data
              if (actData.userId) {
                try {
                  const actDoc = await getDoc(doc(db, 'users', actData.userId));
                  if (actDoc.exists()) {
                    const userData = actDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...actData,
                      name: userData.fullName || '',
                      username: userData.userName || '',
                      photoUrl: userData.profilePictureUrl || '',
                      subType: userData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${actData.userId}:`, error);
                }
              } else if (actData.type === 'band' && actData.id) {
                try {
                  const bandDoc = await getDoc(doc(db, 'bands', actData.id));
                  if (bandDoc.exists()) {
                    const bandData = bandDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...actData,
                      name: bandData.bandFullName || '',
                      username: bandData.bandUserName || '',
                      photoUrl: bandData.bandProfilePictureUrl || '',
                      genre: bandData.bandGenres?.join(', ') || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching band data for ${actData.id}:`, error);
                }
              }
              
              // Return the act data as is if enrichment failed
              return {
                id: docSnapshot.id,
                ...actData
              };
            })
          );
          
          setPastActs(allPastActs);
        } else {
          setPastActs(initialPastActs);
        }
      }
    } catch (error) {
      console.error('Error fetching managing acts:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load managing acts',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleActClick = (act) => {
    if (act.type === 'band' && act.username) {
      navigate(`/bands/${act.username}`);
    } else if (act.username) {
      navigate(`/${act.username}`);
    } else if (act.userId) {
      navigate(`/users/${act.userId}`);
    } else if (act.type === 'band' && act.id) {
      navigate(`/bands/${act.id}`);
    }
  };
  
  const handleLoadMore = async (isPast = false) => {
    if (loadingMore || (!hasMoreCurrent && !isPast) || (!hasMorePast && isPast) || !profileData?.userId) return;
    
    setLoadingMore(true);
    try {
      if (isPast) {
        // Load more past acts from the full list
        const nextBatch = pastActs.slice(
          displayedPastActs.length,
          displayedPastActs.length + 3
        );
        
        setDisplayedPastActs(prev => [...prev, ...nextBatch]);
        
        // Check if there are more past acts to load
        setHasMorePast(displayedPastActs.length + nextBatch.length < pastActs.length);
      } else {
        // Load more current acts from the full list
        const nextBatch = currentActs.slice(
          displayedCurrentActs.length,
          displayedCurrentActs.length + 3
        );
        
        setDisplayedCurrentActs(prev => [...prev, ...nextBatch]);
        
        // Check if there are more current acts to load
        setHasMoreCurrent(displayedCurrentActs.length + nextBatch.length < currentActs.length);
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} acts:`, error);
      showCustomAlert({
        title: 'Error',
        message: `Failed to load more ${isPast ? 'past' : 'current'} acts`,
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };
  
  const handleRemoveAct = async (act, isPast = false) => {
    if (!window.confirm(`Are you sure you want to remove ${act.name || 'this act'}?`)) {
      return;
    }
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', profileData.userId);
      
      // 1. Remove from subcollection
      const actRef = doc(db, 'users', profileData.userId, 'managingActs', act.id);
      await deleteDoc(actRef);
      
      // 2. Update the main user document
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // 3. Get new top 3 acts for main document
        const actsRef = collection(db, 'users', profileData.userId, 'managingActs');
        const top3Query = query(
          actsRef,
          where('isPast', '==', isPast),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const top3Snapshot = await getDocs(top3Query);
        const top3Acts = top3Snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // 4. Update main document with top3 and the count
        if (isPast) {
          await updateDoc(userRef, {
            pastManagingActs: top3Acts,
            pastManagingActsCount: Math.max(0, (userData.pastManagingActsCount || 0) - 1)
          });
        } else {
          await updateDoc(userRef, {
            managingActs: top3Acts,
            managingActsCount: Math.max(0, (userData.managingActsCount || 0) - 1)
          });
        }
        
        // 5. If it's a current act, update the artist's managedBy array
        if (!isPast && act.userId) {
          try {
            // First check if there's an entry in the user's managers subcollection
            const managerRef = doc(db, 'users', act.userId, 'managers', profileData.userId);
            await deleteDoc(managerRef);
            
            // Then update the user's managedBy array
            const actUserRef = doc(db, 'users', act.userId);
            const actUserDoc = await getDoc(actUserRef);
            
            if (actUserDoc.exists()) {
              const actUserData = actUserDoc.data();
              const updatedManagedBy = (actUserData.managedBy || []).filter(
                manager => manager.userId !== profileData.userId
              );
              
              // Get new top 3 managers
              const managersRef = collection(db, 'users', act.userId, 'managers');
              const top3ManagersQuery = query(managersRef, orderBy('joinedAt', 'desc'), limit(3));
              const top3ManagersSnapshot = await getDocs(top3ManagersQuery);
              const top3Managers = top3ManagersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              
              // Update artist's document
              await updateDoc(actUserRef, {
                managedBy: top3Managers,
                managedByCount: Math.max(0, (actUserData.managedByCount || 0) - 1)
              });
              
              // Create notification for the artist
              await createNotification(act.userId, 'general', {
                type: 'MANAGER_ARTIST_REMOVAL',
                title: 'Manager Removal',
                message: `${userData.fullName || 'A manager'} has removed you from their managing acts.`,
                data: {
                  userId: profileData.userId,
                  userName: userData.userName,
                  fullName: userData.fullName,
                  _displayData: {
                    userName: userData.userName,
                    fullName: userData.fullName
                  }
                }
              });
            }
          } catch (e) {
            console.error('Error updating artist document:', e);
            // Continue even if this fails
          }
        }
        
        // 6. Update UI state
        if (isPast) {
          const updatedPastActs = pastActs.filter(a => a.id !== act.id);
          setPastActs(updatedPastActs);
          setDisplayedPastActs(displayedPastActs.filter(a => a.id !== act.id));
          setHasMorePast((userData.pastManagingActsCount || 0) - 1 > top3Acts.length);
        } else {
          const updatedCurrentActs = currentActs.filter(a => a.id !== act.id);
          setCurrentActs(updatedCurrentActs);
          setDisplayedCurrentActs(displayedCurrentActs.filter(a => a.id !== act.id));
          setHasMoreCurrent((userData.managingActsCount || 0) - 1 > top3Acts.length);
        }
        
        showCustomAlert({
          title: 'Success',
          message: `Successfully removed ${act.name || 'act'} from ${isPast ? 'past' : 'current'} managing acts`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error removing managed act:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to remove managed act',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    
    // Get month names
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const startMonthName = months[parseInt(startMonth) - 1] || '';
    const endMonthName = months[parseInt(endMonth) - 1] || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };
  
  return (
    <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
      <h3 className={styles.sectionTitle}>Managing Acts</h3>
      
      {/* Tabs for Current/Past */}
      <div className={styles.actsTabs}>
        <button 
          className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading {activeTab} managing acts...</p>
        </div>
      ) : activeTab === 'current' ? (
        displayedCurrentActs.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedCurrentActs.map(act => (
              <div 
                key={act.id || `${act.type}-${act.userId || act.bandId}`}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleActClick(act)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={act.photoUrl || defaultProfilePhoto}
                      alt={act.name}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{act.name}</h4>
                      </div>
                      
                      {act.username && (
                        <p className={styles.actUsername}>@{act.username}</p>
                      )}
                      
                      <div className={styles.actType}>
                        {act.type === 'band' ? (
                          <span className={styles.bandBadge}>
                            <FontAwesomeIcon icon={faMusic} /> Band
                          </span>
                        ) : (
                          <span className={styles.artistBadge}>
                            <FontAwesomeIcon icon={faUser} /> {act.subType || 'Artist'}
                          </span>
                        )}
                      </div>
                      
                      {act.role && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserShield} />
                          <span>as a {act.role}</span>
                        </div>
                      )}
                      
                      {act.since && (
                        <div className={styles.sinceBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>Since {act.since.month}/{act.since.year}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveAct(act, false);
                        }}
                        title="Remove act"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMoreCurrent && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(false)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No current managing acts.</p>
            {isOwnProfile && (
              <p className={styles.emptyStateMessage}>
                Artists will appear here when you add them as managing acts.
              </p>
            )}
          </div>
        )
      ) : (
        // Past acts
        displayedPastActs.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedPastActs.map(act => (
              <div 
                key={act.id || `past-${act.type}-${act.userId || act.bandId}`}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleActClick(act)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={act.photoUrl || defaultProfilePhoto}
                      alt={act.name}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{act.name}</h4>
                      </div>
                      
                      {act.username && (
                        <p className={styles.actUsername}>@{act.username}</p>
                      )}
                      
                      <div className={styles.actType}>
                        {act.type === 'band' ? (
                          <span className={styles.bandBadge}>
                            <FontAwesomeIcon icon={faMusic} /> Band
                          </span>
                        ) : (
                          <span className={styles.artistBadge}>
                            <FontAwesomeIcon icon={faUser} /> {act.subType || 'Artist'}
                          </span>
                        )}
                      </div>
                      
                      {act.role && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserShield} />
                          <span>as a {act.role}</span>
                        </div>
                      )}
                      
                      {act.duration && (
                        <div className={styles.durationBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{formatDuration(act.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveAct(act, true);
                        }}
                        title="Remove act"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {hasMorePast && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(true)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No past managing acts.</p>
            {isOwnProfile && (
              <p className={styles.emptyStateMessage}>
                Past managing acts will appear here when you add them.
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
};
  

const ManagedBySection = ({ profileData, isOwnProfile, showCustomAlert, navigate }) => {
  const [activeTab, setActiveTab] = useState('current');
  const [currentManagers, setCurrentManagers] = useState([]);
  const [pastManagers, setPastManagers] = useState([]);
  const [displayedCurrentManagers, setDisplayedCurrentManagers] = useState([]);
  const [displayedPastManagers, setDisplayedPastManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  const { userData } = useContext(UserContext);
  
  useEffect(() => {
    if (profileData?.userId) {
      fetchManagedBy(profileData.userId);
    }
  }, [profileData?.userId]);
  
  const fetchManagedBy = async (userId) => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // First get the user document to get manager count and top 3 managers
      const userDoc = await getDoc(doc(db, 'users', userId));
      let initialCurrentManagers = [];
      let initialPastManagers = [];
      let currentCount = 0;
      let pastCount = 0;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        initialCurrentManagers = userData.managedBy || [];
        initialPastManagers = userData.pastManagedBy || [];
        currentCount = userData.managedByCount || 0;
        pastCount = userData.pastManagedByCount || 0;
        
        // Enrich current managers with additional data if needed
        if (initialCurrentManagers.length > 0) {
          initialCurrentManagers = await Promise.all(
            initialCurrentManagers.map(async (manager) => {
              if (manager.userId && (!manager.fullName || !manager.userName)) {
                try {
                  const managerDoc = await getDoc(doc(db, 'users', manager.userId));
                  if (managerDoc.exists()) {
                    const managerData = managerDoc.data();
                    return {
                      ...manager,
                      fullName: managerData.fullName || '',
                      userName: managerData.userName || '',
                      profilePictureUrl: managerData.profilePictureUrl || '',
                      userSubType: managerData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching manager data for ${manager.userId}:`, error);
                }
              }
              return manager;
            })
          );
        }
        
        // Enrich past managers with additional data if needed
        if (initialPastManagers.length > 0) {
          initialPastManagers = await Promise.all(
            initialPastManagers.map(async (manager) => {
              if (manager.userId && (!manager.fullName || !manager.userName)) {
                try {
                  const managerDoc = await getDoc(doc(db, 'users', manager.userId));
                  if (managerDoc.exists()) {
                    const managerData = managerDoc.data();
                    return {
                      ...manager,
                      fullName: managerData.fullName || '',
                      userName: managerData.userName || '',
                      profilePictureUrl: managerData.profilePictureUrl || '',
                      userSubType: managerData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching past manager data for ${manager.userId}:`, error);
                }
              }
              return manager;
            })
          );
        }
      }
      
      // Set initial displayed managers
      setDisplayedCurrentManagers(initialCurrentManagers);
      setDisplayedPastManagers(initialPastManagers);
      
      // Check if there are more managers than what's displayed
      setHasMoreCurrent(currentCount > initialCurrentManagers.length);
      setHasMorePast(pastCount > initialPastManagers.length);
      
      // Fetch all current managers from subcollection
      if (initialCurrentManagers.length > 0) {
        const managedByRef = collection(db, 'users', userId, 'managedBy');
        const currentQuery = query(managedByRef, where('isPast', '==', false));
        const currentSnapshot = await getDocs(currentQuery);
        
        if (!currentSnapshot.empty) {
          const allCurrentManagers = await Promise.all(
            currentSnapshot.docs.map(async (docSnapshot) => {
              const managerData = docSnapshot.data();
              
              // Enrich with manager data
              if (managerData.userId) {
                try {
                  const managerDoc = await getDoc(doc(db, 'users', managerData.userId));
                  if (managerDoc.exists()) {
                    const userData = managerDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...managerData,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${managerData.userId}:`, error);
                }
              }
              
              // Return the manager data as is if enrichment failed
              return {
                id: docSnapshot.id,
                ...managerData
              };
            })
          );
          
          setCurrentManagers(allCurrentManagers);
        } else {
          setCurrentManagers(initialCurrentManagers);
        }
      }
      
      // Fetch all past managers from subcollection
      if (initialPastManagers.length > 0) {
        const managedByRef = collection(db, 'users', userId, 'managedBy');
        const pastQuery = query(managedByRef, where('isPast', '==', true));
        const pastSnapshot = await getDocs(pastQuery);
        
        if (!pastSnapshot.empty) {
          const allPastManagers = await Promise.all(
            pastSnapshot.docs.map(async (docSnapshot) => {
              const managerData = docSnapshot.data();
              
              // Enrich with manager data
              if (managerData.userId) {
                try {
                  const managerDoc = await getDoc(doc(db, 'users', managerData.userId));
                  if (managerDoc.exists()) {
                    const userData = managerDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...managerData,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${managerData.userId}:`, error);
                }
              }
              
              // Return the manager data as is if enrichment failed
              return {
                id: docSnapshot.id,
                ...managerData
              };
            })
          );
          
          setPastManagers(allPastManagers);
        } else {
          setPastManagers(initialPastManagers);
        }
      }
    } catch (error) {
      console.error('Error fetching managed by:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load managers',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleManagerClick = (manager) => {
    if (manager.userName) {
      navigate(`/${manager.userName}`);
    } else if (manager.userId) {
      // Navigate to manager profile by ID
      navigate(`/users/${manager.userId}`);
    }
  };
  
  const handleLoadMore = async (isPast = false) => {
    if (loadingMore || (!hasMoreCurrent && !isPast) || (!hasMorePast && isPast) || !profileData?.userId) return;
    
    setLoadingMore(true);
    try {
      if (isPast) {
        // Load more past managers from the full list
        const nextBatch = pastManagers.slice(
          displayedPastManagers.length,
          displayedPastManagers.length + 3
        );
        
        setDisplayedPastManagers(prev => [...prev, ...nextBatch]);
        
        // Check if there are more past managers to load
        setHasMorePast(displayedPastManagers.length + nextBatch.length < pastManagers.length);
      } else {
        // Load more current managers from the full list
        const nextBatch = currentManagers.slice(
          displayedCurrentManagers.length,
          displayedCurrentManagers.length + 3
        );
        
        setDisplayedCurrentManagers(prev => [...prev, ...nextBatch]);
        
        // Check if there are more current managers to load
        setHasMoreCurrent(displayedCurrentManagers.length + nextBatch.length < currentManagers.length);
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} managers:`, error);
      showCustomAlert({
        title: 'Error',
        message: `Failed to load more ${isPast ? 'past' : 'current'} managers`,
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };
  
  const handleRemoveManager = async (manager, isPast = false) => {
    if (!window.confirm(`Are you sure you want to remove ${manager.fullName || 'this manager'}?`)) {
      return;
    }
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', profileData.userId);
      
      // 1. Remove from subcollection
      const managerRef = doc(db, 'users', profileData.userId, 'managedBy', manager.id);
      await deleteDoc(managerRef);
      
      // 2. Update the main user document
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // 3. Get new top 3 managers for main document
        const managersRef = collection(db, 'users', profileData.userId, 'managedBy');
        const top3Query = query(
          managersRef,
          where('isPast', '==', isPast),
          orderBy('joinedAt', 'desc'),
          limit(3)
        );
        const top3Snapshot = await getDocs(top3Query);
        const top3Managers = top3Snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // 4. Update main document with top3 and the count
        if (isPast) {
          await updateDoc(userRef, {
            pastManagedBy: top3Managers,
            pastManagedByCount: Math.max(0, (userData.pastManagedByCount || 0) - 1)
          });
        } else {
          await updateDoc(userRef, {
            managedBy: top3Managers,
            managedByCount: Math.max(0, (userData.managedByCount || 0) - 1)
          });
        }
        
        // 5. Also update the manager's managingActs collection
        if (!isPast) {
          try {
            // Find the corresponding entry in the manager's managingActs subcollection
            const managingActsQuery = query(
              collection(db, 'users', manager.userId, 'managingActs'),
              where(profileData.userType === 'Band' ? 'bandId' : 'userId', '==', profileData.userId)
            );
            const managingActsSnapshot = await getDocs(managingActsQuery);
            
            if (!managingActsSnapshot.empty) {
              // Remove the entry
              for (const doc of managingActsSnapshot.docs) {
                await deleteDoc(doc.ref);
              }
              
              // Update the manager's managingActs array
              const managerRef = doc(db, 'users', manager.userId);
              const managerDoc = await getDoc(managerRef);
              
              if (managerDoc.exists()) {
                const managerData = managerDoc.data();
                
                // Get new top 3 managing acts
                const managingActsRef = collection(db, 'users', manager.userId, 'managingActs');
                const top3ActsQuery = query(
                  managingActsRef, 
                  where('isPast', '==', false),
                  orderBy('createdAt', 'desc'), 
                  limit(3)
                );
                const top3ActsSnapshot = await getDocs(top3ActsQuery);
                const top3Acts = top3ActsSnapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                
                // Update manager's document
                await updateDoc(managerRef, {
                  managingActs: top3Acts,
                  managingActsCount: Math.max(0, (managerData.managingActsCount || 0) - 1)
                });
              }
            }
          } catch (e) {
            console.error('Error updating manager document:', e);
            // Continue even if this fails
          }
          
          // Create notification for the manager
          await createNotification(manager.userId, 'general', {
            type: 'ARTIST_MANAGER_REMOVAL',
            title: 'Artist Removal',
            message: `${userData.fullName || 'An artist'} has removed you as their manager.`,
            data: {
              userId: profileData.userId,
              userName: userData.userName,
              fullName: userData.fullName,
              _displayData: {
                userName: userData.userName,
                fullName: userData.fullName
              }
            }
          });
        }
        
        // 6. Update UI state
        if (isPast) {
          const updatedPastManagers = pastManagers.filter(m => m.id !== manager.id);
          setPastManagers(updatedPastManagers);
          setDisplayedPastManagers(displayedPastManagers.filter(m => m.id !== manager.id));
          setHasMorePast((userData.pastManagedByCount || 0) - 1 > top3Managers.length);
        } else {
          const updatedCurrentManagers = currentManagers.filter(m => m.id !== manager.id);
          setCurrentManagers(updatedCurrentManagers);
          setDisplayedCurrentManagers(displayedCurrentManagers.filter(m => m.id !== manager.id));
          setHasMoreCurrent((userData.managedByCount || 0) - 1 > top3Managers.length);
        }
        
        showCustomAlert({
          title: 'Success',
          message: `Successfully removed ${manager.fullName || 'manager'} from ${isPast ? 'past' : 'current'} managers`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error removing manager:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to remove manager',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    
    // Get month names
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const startMonthName = months[parseInt(startMonth) - 1] || '';
    const endMonthName = months[parseInt(endMonth) - 1] || '';
    
    return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`;
  };
  
  // Format since for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    
    // Get month names
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthName = months[parseInt(month) - 1] || '';
    
    return `Since ${monthName} ${year}`;
  };
  
  // Navigate to add managedBy page
  const handleAddManager = () => {
    navigate('/edit-profile');
  };
  
  return (
    <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
      <h3 className={styles.sectionTitle}>Managed By</h3>
      
      {/* Tabs for Current/Past */}
      <div className={styles.actsTabs}>
        <button 
          className={`${styles.actsTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.actsTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      {isOwnProfile && (
        <div className={styles.buttonContainer}>
          <button 
            className={styles.actionButton}
            onClick={handleAddManager}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add {activeTab === 'past' ? 'Past ' : ''}Manager
          </button>
        </div>
      )}
      
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading {activeTab} managers...</p>
        </div>
      ) : activeTab === 'current' ? (
        displayedCurrentManagers.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedCurrentManagers.map(manager => (
              <div 
                key={manager.id || manager.userId}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleManagerClick(manager)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={manager.profilePictureUrl || defaultProfilePhoto}
                      alt={manager.fullName}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{manager.fullName}</h4>
                      </div>
                      
                      {manager.userName && (
                        <p className={styles.actUsername}>@{manager.userName}</p>
                      )}
                      
                      {manager.userSubType && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserTie} />
                          <span>{manager.userSubType}</span>
                        </div>
                      )}
                      
                      {manager.role && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserShield} />
                          <span>as {manager.role}</span>
                        </div>
                      )}
                      
                      {manager.since && (
                        <div className={styles.sinceBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>Since {manager.since.month}/{manager.since.year}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveManager(manager, false);
                        }}
                        title="Remove manager"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isOwnProfile && (
              <div className={styles.addCard} onClick={handleAddManager}>
                <div className={styles.addCardContent}>
                  <FontAwesomeIcon icon={faPlus} className={styles.addIcon} />
                  <p>Add Manager</p>
                </div>
              </div>
            )}
            
            {hasMoreCurrent && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(false)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No current managers.</p>
            {isOwnProfile && (
              <div className={styles.addCard} onClick={handleAddManager}>
                <div className={styles.addCardContent}>
                  <FontAwesomeIcon icon={faPlus} className={styles.addIcon} />
                  <p>Add Your First Manager</p>
                </div>
              </div>
            )}
          </div>
        )
      ) : (
        // Past managers
        displayedPastManagers.length > 0 ? (
          <div className={styles.associatedActsGrid}>
            {displayedPastManagers.map(manager => (
              <div 
                key={manager.id || `past-${manager.userId}`}
                className={styles.actCard}
              >
                <div className={styles.actCardContent}>
                  <div
                    className={styles.actInfo}
                    onClick={() => handleManagerClick(manager)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={manager.profilePictureUrl || defaultProfilePhoto}
                      alt={manager.fullName}
                      className={styles.actPhoto}
                      onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                    />
                    <div className={styles.actInfoText}>
                      <div className={styles.actNameContainer}>
                        <h4>{manager.fullName}</h4>
                      </div>
                      
                      {manager.userName && (
                        <p className={styles.actUsername}>@{manager.userName}</p>
                      )}
                      
                      {manager.userSubType && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserTie} />
                          <span>{manager.userSubType}</span>
                        </div>
                      )}
                      
                      {manager.role && (
                        <div className={styles.roleBadge}>
                          <FontAwesomeIcon icon={faUserShield} />
                          <span>as {manager.role}</span>
                        </div>
                      )}
                      
                      {manager.duration && (
                        <div className={styles.durationBadge}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          <span>{formatDuration(manager.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {isOwnProfile && (
                    <div className={styles.cardActions}>
                      <button 
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveManager(manager, true);
                        }}
                        title="Remove manager"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isOwnProfile && (
              <div className={styles.addCard} onClick={handleAddManager}>
                <div className={styles.addCardContent}>
                  <FontAwesomeIcon icon={faPlus} className={styles.addIcon} />
                  <p>Add Past Manager</p>
                </div>
              </div>
            )}
            
            {hasMorePast && (
              <button 
                className={styles.loadMoreButton}
                onClick={() => handleLoadMore(true)}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Loading...</span>
                  </>
                ) : (
                  <span>Load More</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No past managers.</p>
            {isOwnProfile && (
              <div className={styles.addCard} onClick={handleAddManager}>
                <div className={styles.addCardContent}>
                  <FontAwesomeIcon icon={faPlus} className={styles.addIcon} />
                  <p>Add Past Manager</p>
                </div>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};


// Updated ManagersSection with proper data fetching and delete functionality
const ManagersSection = ({ profileData, isOwnProfile, showCustomAlert, navigate }) => {
  const [managers, setManagers] = useState([]);
  const [displayedManagers, setDisplayedManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const { userData } = useContext(UserContext);
  
  useEffect(() => {
    if (profileData?.userId) {
      fetchManagers(profileData.userId);
    }
  }, [profileData?.userId]);
  
  const fetchManagers = async (userId) => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // First get the user document to get managers count and top 3 managers
      const userDoc = await getDoc(doc(db, 'users', userId));
      let initialManagers = [];
      let totalCount = 0;
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        initialManagers = userData.managedBy || []; // "managedBy" for artists, "managers" for other users
        totalCount = userData.managedByCount || userData.managersCount || 0;
        
        // Enrich with manager data if needed
        if (initialManagers.length > 0) {
          initialManagers = await Promise.all(
            initialManagers.map(async (manager) => {
              // Only fetch full manager details if we just have the ID
              if (manager.userId && (!manager.fullName || !manager.userName)) {
                try {
                  const managerDoc = await getDoc(doc(db, 'users', manager.userId));
                  if (managerDoc.exists()) {
                    const managerData = managerDoc.data();
                    return {
                      ...manager,
                      fullName: managerData.fullName || '',
                      userName: managerData.userName || '',
                      profilePictureUrl: managerData.profilePictureUrl || '',
                      userSubType: managerData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching manager data for ${manager.userId}:`, error);
                }
              }
              return manager;
            })
          );
        }
      }
      
      // Set initial displayed managers
      setDisplayedManagers(initialManagers);
      
      // Check if there are more managers than what's displayed
      setHasMore(totalCount > initialManagers.length);
      
      // If there are managers, fetch all from subcollection for complete data
      if (initialManagers.length > 0) {
        const managersRef = collection(db, 'users', userId, 'managers');
        const snapshot = await getDocs(managersRef);
        
        if (!snapshot.empty) {
          const allManagers = await Promise.all(
            snapshot.docs.map(async (docSnapshot) => {
              const managerData = docSnapshot.data();
              
              // Enrich with manager user data
              if (managerData.userId) {
                try {
                  const managerUserDoc = await getDoc(doc(db, 'users', managerData.userId));
                  if (managerUserDoc.exists()) {
                    const managerUserData = managerUserDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...managerData,
                      fullName: managerUserData.fullName || '',
                      userName: managerUserData.userName || '',
                      profilePictureUrl: managerUserData.profilePictureUrl || '',
                      userSubType: managerUserData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching manager user data for ${managerData.userId}:`, error);
                }
              }
              
              // Return the manager data as is if user data enrichment failed
              return {
                id: docSnapshot.id,
                ...managerData
              };
            })
          );
          
          // Store all managers
          setManagers(allManagers);
        } else {
          setManagers(initialManagers);
        }
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load managers',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleManagerClick = (manager) => {
    if (manager.userName) {
      navigate(`/${manager.userName}`);
    } else if (manager.userId) {
      // Navigate to manager profile by ID
      navigate(`/users/${manager.userId}`);
    }
  };
  
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || !profileData?.userId) return;
    
    setLoadingMore(true);
    try {
      // Load more managers from the full list
      const nextBatch = managers.slice(
        displayedManagers.length,
        displayedManagers.length + 3
      );
      
      setDisplayedManagers(prev => [...prev, ...nextBatch]);
      
      // Check if there are more managers to load
      setHasMore(displayedManagers.length + nextBatch.length < managers.length);
    } catch (error) {
      console.error('Error loading more managers:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to load more managers',
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };
  
  const handleRemoveManager = async (managerId, managerName) => {
    if (!window.confirm(`Are you sure you want to remove ${managerName || 'this manager'}?`)) {
      return;
    }
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', profileData.userId);
      
      // 1. Remove from subcollection
      const managerRef = doc(db, 'users', profileData.userId, 'managers', managerId);
      await deleteDoc(managerRef);
      
      // 2. Update the main user document by removing from the managedBy array
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Not actually using this variable anymore, so we can remove it
        // const updatedManagers = (userData.managedBy || []).filter(
        //   manager => manager.userId !== managerId
        // );
        
        // 3. Get new top 3 managers for main document
        const managersRef = collection(db, 'users', profileData.userId, 'managers');
        const top3Query = query(managersRef, orderBy('joinedAt', 'desc'), limit(3));
        const top3Snapshot = await getDocs(top3Query);
        const top3Managers = top3Snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // 4. Update main document with top 3 and decrement count
        await updateDoc(userRef, {
          managedBy: top3Managers,
          managedByCount: Math.max(0, (userData.managedByCount || 0) - 1)
        });
        
        // 5. Update manager's managing acts subcollection
        try {
          const managingActRef = doc(db, 'users', managerId, 'managingActs', profileData.userId);
          await deleteDoc(managingActRef);
          
          // Also update the manager's managingActs array
          const managerDoc = await getDoc(doc(db, 'users', managerId));
          if (managerDoc.exists()) {
            const managerData = managerDoc.data();
            const updatedManagingActs = (managerData.managingActs || []).filter(
              act => act.userId !== profileData.userId
            );
            
            // Get new top 3 managing acts
            const managingActsRef = collection(db, 'users', managerId, 'managingActs');
            const top3ActsQuery = query(managingActsRef, orderBy('createdAt', 'desc'), limit(3));
            const top3ActsSnapshot = await getDocs(top3ActsQuery);
            const top3Acts = top3ActsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Update manager's document
            await updateDoc(doc(db, 'users', managerId), {
              managingActs: top3Acts,
              managingActsCount: Math.max(0, (managerData.managingActsCount || 0) - 1)
            });
          }
        } catch (e) {
          console.error('Error updating manager document:', e);
          // Continue even if this fails
        }
        
        // 6. Create removal notification for the manager
        await createNotification(managerId, 'general', {
          type: 'ARTIST_MANAGER_REMOVAL',
          title: 'Artist Removal',
          message: `${userData.fullName || 'An artist'} has removed you as their manager.`,
          data: {
            userId: profileData.userId,
            userName: userData.userName,
            fullName: userData.fullName,
            _displayData: {
              userName: userData.userName,
              fullName: userData.fullName
            }
          }
        });
        
        // 7. Update UI state - renamed the variable to fix the error
        const filteredManagers = managers.filter(manager => manager.userId !== managerId);
        setManagers(filteredManagers);
        setDisplayedManagers(displayedManagers.filter(
          manager => manager.userId !== managerId
        ));
        
        showCustomAlert({
          title: 'Success',
          message: `Successfully removed ${managerName || 'manager'}`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error removing manager:', error);
      showCustomAlert({
        title: 'Error',
        message: 'Failed to remove manager',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
      <h3 className={styles.sectionTitle}>Managers</h3>
      
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading managers...</p>
        </div>
      ) : displayedManagers.length > 0 ? (
        <div className={styles.associatedActsGrid}>
          {displayedManagers.map(manager => (
            <div 
              key={manager.id || manager.userId}
              className={styles.actCard}
            >
              <div className={styles.actCardContent}>
                <div
                  className={styles.actInfo}
                  onClick={() => handleManagerClick(manager)}
                  style={{ cursor: 'pointer' }}
                >
                  <img 
                    src={manager.photoUrl || manager.profilePictureUrl || defaultProfilePhoto}
                    alt={manager.name || manager.fullName}
                    className={styles.actPhoto}
                    onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                  />
                  <div className={styles.actInfoText}>
                    <div className={styles.actNameContainer}>
                      <h4>{manager.name || manager.fullName}</h4>
                    </div>
                    
                    {(manager.username || manager.userName) && (
                      <p className={styles.actUsername}>@{manager.username || manager.userName}</p>
                    )}
                    
                    {manager.role && (
                      <div className={styles.roleBadge}>
                        <FontAwesomeIcon icon={faUserTie} />
                        <span>{manager.role}</span>
                      </div>
                    )}
                    
                    {manager.since && (
                      <div className={styles.sinceBadge}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>Since {manager.since.month}/{manager.since.year}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {isOwnProfile && (
                  <div className={styles.cardActions}>
                    <button 
                      className={styles.removeButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveManager(manager.id || manager.userId, manager.name || manager.fullName);
                      }}
                      title="Remove manager"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {hasMore && (
            <button 
              className={styles.loadMoreButton}
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Loading...</span>
                </>
              ) : (
                <span>Load More</span>
              )}
            </button>
          )}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p>No managers yet.</p>
          {isOwnProfile && (
            <p className={styles.emptyStateMessage}>
              Managers will appear here when you accept manager requests.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
  
  // New Component for Executives Section with Modal
  const ExecutivesSection = ({ profileData, isOwnProfile, showCustomAlert }) => {
    const [executives, setExecutives] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const { authToken } = useContext(UserContext);
    
    // Form state
    const [formData, setFormData] = useState({
      name: '',
      role: '',
      email: '',
      phone: '',
      profilePicture: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    
    useEffect(() => {
      if (profileData?.userId) {
        fetchExecutives(profileData.userId);
      }
    }, [profileData?.userId]);
    
    const fetchExecutives = async (userId) => {
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        const executivesRef = collection(db, 'users', userId, 'executives');
        const snapshot = await getDocs(executivesRef);
        
        if (!snapshot.empty) {
          const executivesList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setExecutives(executivesList);
        } else {
          setExecutives([]);
        }
      } catch (error) {
        console.error('Error fetching executives:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setFormData({...formData, profilePicture: file});
        
        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    };
    
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData({...formData, [name]: value});
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!formData.name || !formData.role) {
        showCustomAlert({
          title: 'Error',
          message: 'Name and role are required fields.',
          type: 'error'
        });
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        const db = getFirestoreInstance();
        
        // First create the executive document to get an ID
        const executiveDocRef = await addDoc(collection(db, 'users', profileData.userId, 'executives'), {
          name: formData.name,
          role: formData.role,
          email: formData.email || '',
          phone: formData.phone || '',
          createdAt: Timestamp.now()
        });
        
        const executiveId = executiveDocRef.id;
        
        let photoUrl = '';
        
        // If there's a profile picture, upload it
        if (formData.profilePicture) {
          const formDataObj = new FormData();
          formDataObj.append('media', formData.profilePicture);
          
          // Set the backblaze path
          const backblazePath = `users/${profileData.userId}/executives/${executiveId}/profilePicture`;
          formDataObj.append('path', backblazePath);
          
          const response = await fetch('/api/uploadMedia', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`
            },
            body: formDataObj
          });
          
          if (response.ok) {
            const result = await response.json();
            photoUrl = result.url;
            
            // Update the executive document with the photo URL
            await updateDoc(executiveDocRef, {
              photoUrl: photoUrl
            });
          }
        }
        
        // Add the new executive to the state
        setExecutives(prev => [...prev, {
          id: executiveId,
          name: formData.name,
          role: formData.role,
          email: formData.email || '',
          phone: formData.phone || '',
          photoUrl: photoUrl,
          createdAt: new Date().toISOString()
        }]);
        
        // Reset form and close modal
        setFormData({
          name: '',
          role: '',
          email: '',
          phone: '',
          profilePicture: null
        });
        setPreviewUrl(null);
        setShowAddModal(false);
        
        showCustomAlert({
          title: 'Success',
          message: 'Executive added successfully!',
          type: 'success'
        });
      } catch (error) {
        console.error('Error adding executive:', error);
        showCustomAlert({
          title: 'Error',
          message: 'Failed to add executive. Please try again.',
          type: 'error'
        });
      } finally {
        setIsSubmitting(false);
      }
    };
    
    const handleExecutiveClick = (executive) => {
      // Show executive details modal
      showCustomAlert({
        title: executive.name,
        message: `
          Role: ${executive.role}
          ${executive.email ? `Email: ${executive.email}` : ''}
          ${executive.phone ? `Phone: ${executive.phone}` : ''}
        `,
        type: 'info'
      });
    };
    
    return (
      <>
        <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
          <h3 className={styles.sectionTitle}>Executives</h3>
          
          {executives.length > 0 ? (
            <div className={styles.associatedActsGrid}>
              {executives.map(executive => (
                <div 
                  key={executive.id}
                  className={styles.actCard}
                  onClick={() => handleExecutiveClick(executive)}
                >
                  <img 
                    src={executive.photoUrl || defaultProfilePhoto}
                    alt={executive.name}
                    className={styles.actPhoto}
                    onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                  />
                  <div className={styles.actInfo}>
                    <div className={styles.actNameContainer}>
                      <h4>{executive.name}</h4>
                    </div>
                    
                    {executive.role && (
                      <div className={styles.roleBadge}>
                        <FontAwesomeIcon icon={faUserTie} />
                        <span>{executive.role}</span>
                      </div>
                    )}
                    
                    {executive.email && (
                      <div className={styles.contactBadge}>
                        <FontAwesomeIcon icon={faEnvelope} />
                        <span>{executive.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isOwnProfile && (
                <button 
                  className={styles.addMoreActsButton}
                  onClick={() => setShowAddModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Executive
                </button>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No executives added yet.</p>
              {isOwnProfile && (
                <button 
                  className={styles.addFirstItemButton}
                  onClick={() => setShowAddModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Executive
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Add Executive Modal */}
        {showAddModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <button 
                className={styles.modalClose}
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({
                    name: '',
                    role: '',
                    email: '',
                    phone: '',
                    profilePicture: null
                  });
                  setPreviewUrl(null);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              
              <h2>Add Executive</h2>
              
              <form onSubmit={handleSubmit}>
                {/* Profile Picture Upload */}
                <div className={styles.formGroup}>
                  <label>Profile Picture</label>
                  <div className={styles.profilePictureUpload}>
                    {previewUrl ? (
                      <div className={styles.previewContainer}>
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          className={styles.previewImage} 
                        />
                        <button 
                          type="button" 
                          className={styles.removePreview}
                          onClick={() => {
                            setPreviewUrl(null);
                            setFormData({...formData, profilePicture: null});
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.uploadPlaceholder}>
                        <FontAwesomeIcon icon={faUser} />
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleFileChange}
                          className={styles.fileInput}
                        />
                        <span>Click to upload</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Name */}
                <div className={styles.formGroup}>
                  <label htmlFor="name">Name*</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                {/* Role */}
                <div className={styles.formGroup}>
                  <label htmlFor="role">Role*</label>
                  <select 
                    id="role" 
                    name="role" 
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a role</option>
                    <option value="Founder">Founder</option>
                    <option value="Co-Founder">Co-Founder</option>
                    <option value="CEO">CEO</option>
                    <option value="Managing Director">Managing Director</option>
                    <option value="Executive Director">Executive Director</option>
                    <option value="Partner">Partner</option>
                    <option value="A&R">A&R</option>
                    <option value="Booking Agent">Booking Agent</option>
                    <option value="Representative">Representative</option>
                  </select>
                </div>
                
                {/* Email */}
                <div className={styles.formGroup}>
                  <label htmlFor="email">Email (Optional)</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                
                {/* Phone */}
                <div className={styles.formGroup}>
                  <label htmlFor="phone">Phone (Optional)</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                
                {/* Submit Button */}
                <div className={styles.formActions}>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowAddModal(false);
                      setFormData({
                        name: '',
                        role: '',
                        email: '',
                        phone: '',
                        profilePicture: null
                      });
                      setPreviewUrl(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSubmitting || !formData.name || !formData.role}
                  >
                    {isSubmitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Saving...
                      </>
                    ) : 'Save Executive'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>
    );
  };
  
  // New Component for Contacts Section with Modal
  const ContactsSection = ({ profileData, isOwnProfile, showCustomAlert }) => {
    const [contacts, setContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const { authToken } = useContext(UserContext);
    
    // Form state
    const [formData, setFormData] = useState({
      name: '',
      role: '',
      email: '',
      phone: '',
      profilePicture: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    
    useEffect(() => {
      if (profileData?.userId) {
        fetchContacts(profileData.userId);
      }
    }, [profileData?.userId]);
    
    const fetchContacts = async (userId) => {
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        const contactsRef = collection(db, 'users', userId, 'contacts');
        const snapshot = await getDocs(contactsRef);
        
        if (!snapshot.empty) {
          const contactsList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setContacts(contactsList);
        } else {
          setContacts([]);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setFormData({...formData, profilePicture: file});
        
        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    };
    
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData({...formData, [name]: value});
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!formData.name || !formData.role) {
        showCustomAlert({
          title: 'Error',
          message: 'Name and role are required fields.',
          type: 'error'
        });
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        const db = getFirestoreInstance();
        
        // First create the contact document to get an ID
        const contactDocRef = await addDoc(collection(db, 'users', profileData.userId, 'contacts'), {
          name: formData.name,
          role: formData.role,
          email: formData.email || '',
          phone: formData.phone || '',
          createdAt: Timestamp.now()
        });
        
        const contactId = contactDocRef.id;
        
        let photoUrl = '';
        
        // If there's a profile picture, upload it
        if (formData.profilePicture) {
          const formDataObj = new FormData();
          formDataObj.append('media', formData.profilePicture);
          
          // Set the backblaze path
          const backblazePath = `users/${profileData.userId}/contacts/${contactId}/profilePicture`;
          formDataObj.append('path', backblazePath);
          
          const response = await fetch('/api/uploadMedia', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`
            },
            body: formDataObj
          });
          
          if (response.ok) {
            const result = await response.json();
            photoUrl = result.url;
            
            // Update the contact document with the photo URL
            await updateDoc(contactDocRef, {
              photoUrl: photoUrl
            });
          }
        }
        
        // Add the new contact to the state
        setContacts(prev => [...prev, {
          id: contactId,
          name: formData.name,
          role: formData.role,
          email: formData.email || '',
          phone: formData.phone || '',
          photoUrl: photoUrl,
          createdAt: new Date().toISOString()
        }]);
        
        // Reset form and close modal
        setFormData({
          name: '',
          role: '',
          email: '',
          phone: '',
          profilePicture: null
        });
        setPreviewUrl(null);
        setShowAddModal(false);
        
        showCustomAlert({
          title: 'Success',
          message: 'Contact added successfully!',
          type: 'success'
        });
      } catch (error) {
        console.error('Error adding contact:', error);
        showCustomAlert({
          title: 'Error',
          message: 'Failed to add contact. Please try again.',
          type: 'error'
        });
      } finally {
        setIsSubmitting(false);
      }
    };
    
    const handleContactClick = (contact) => {
      // Show contact details modal
      showCustomAlert({
        title: contact.name,
        message: `
          Role: ${contact.role}
          ${contact.email ? `Email: ${contact.email}` : ''}
          ${contact.phone ? `Phone: ${contact.phone}` : ''}
        `,
        type: 'info'
      });
    };
    
    return (
      <>
        <div className={`${styles.associatedActsSection} ${styles.fadeIn}`}>
          <h3 className={styles.sectionTitle}>Contacts</h3>
          
          {contacts.length > 0 ? (
            <div className={styles.associatedActsGrid}>
              {contacts.map(contact => (
                <div 
                  key={contact.id}
                  className={styles.actCard}
                  onClick={() => handleContactClick(contact)}
                >
                  <img 
                    src={contact.photoUrl || defaultProfilePhoto}
                    alt={contact.name}
                    className={styles.actPhoto}
                    onError={(e) => { e.target.onerror = null; e.target.src = defaultProfilePhoto; }}
                  />
                  <div className={styles.actInfo}>
                    <div className={styles.actNameContainer}>
                      <h4>{contact.name}</h4>
                    </div>
                    
                    {contact.role && (
                      <div className={styles.roleBadge}>
                        <FontAwesomeIcon icon={faIdCard} />
                        <span>{contact.role}</span>
                      </div>
                    )}
                    
                    {contact.email && (
                      <div className={styles.contactBadge}>
                        <FontAwesomeIcon icon={faEnvelope} />
                        <span>{contact.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isOwnProfile && (
                <button 
                  className={styles.addMoreActsButton}
                  onClick={() => setShowAddModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Contact
                </button>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No contacts added yet.</p>
              {isOwnProfile && (
                <button 
                  className={styles.addFirstItemButton}
                  onClick={() => setShowAddModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Contact
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Add Contact Modal */}
        {showAddModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <button 
                className={styles.modalClose}
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({
                    name: '',
                    role: '',
                    email: '',
                    phone: '',
                    profilePicture: null
                  });
                  setPreviewUrl(null);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              
              <h2>Add Contact</h2>
              
              <form onSubmit={handleSubmit}>
                {/* Profile Picture Upload */}
                <div className={styles.formGroup}>
                  <label>Profile Picture</label>
                  <div className={styles.profilePictureUpload}>
                    {previewUrl ? (
                      <div className={styles.previewContainer}>
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          className={styles.previewImage} 
                        />
                        <button 
                          type="button" 
                          className={styles.removePreview}
                          onClick={() => {
                            setPreviewUrl(null);
                            setFormData({...formData, profilePicture: null});
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ) : (
                      <div className={styles.uploadPlaceholder}>
                        <FontAwesomeIcon icon={faUser} />
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleFileChange}
                          className={styles.fileInput}
                        />
                        <span>Click to upload</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Name */}
                <div className={styles.formGroup}>
                  <label htmlFor="name">Name*</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                {/* Role */}
                <div className={styles.formGroup}>
                  <label htmlFor="role">Role*</label>
                  <select 
                    id="role" 
                    name="role" 
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a role</option>
                    <option value="Bookings">Bookings</option>
                    <option value="PR">PR</option>
                    <option value="Legal">Legal</option>
                    <option value="Sync Licensing">Sync Licensing</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Management">Management</option>
                    <option value="Distribution">Distribution</option>
                    <option value="Publishing">Publishing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                {/* Email */}
                <div className={styles.formGroup}>
                  <label htmlFor="email">Email (Optional)</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                
                {/* Phone */}
                <div className={styles.formGroup}>
                  <label htmlFor="phone">Phone (Optional)</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                
                {/* Submit Button */}
                <div className={styles.formActions}>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowAddModal(false);
                      setFormData({
                        name: '',
                        role: '',
                        email: '',
                        phone: '',
                        profilePicture: null
                      });
                      setPreviewUrl(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSubmitting || !formData.name || !formData.role}
                  >
                    {isSubmitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Saving...
                      </>
                    ) : 'Save Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>
    );
  };